import { Box } from '@mui/material';
import { Bullet, PoolTable, PoolTableColumn, PoolTextField, PoolTypography, ProductDetails } from '@synergy/components';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Locale, UserRole } from '@synergy/models';
import { MouseEvent } from 'react';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import { getCurrencySymbol, hasRole } from '@synergy/utils';
import dayjs from 'dayjs';

import { AppState } from '../../../store/models/app-state';
import { ConvertReceiptCustomerSupplierFooter } from './convert-receipt-customer-supplier-footer';
import { ConvertReceiptLocked } from './convert-receipt-locked';
import { ConverterCustomer } from '../models/internal/converter-customer';
import { ConverterCustomerSupplier } from '../models/internal/converter-customer-supplier';
import { ConverterJob } from '../models/internal/converterJob';
import { ConverterPositionItem } from '../models/internal/converter-positionItem';
import { FinancialCalculations } from '../../../common/utils/financial-calculations';
import { PaloBestOfferBadge } from '../../inquiries/components/palo-best-offer-badge';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { User } from '../../../common/models/user';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';

const styles = () =>
    createStyles({
        input: {
            width: 120,
            '& > div > input': {
                textAlign: 'right',
            },
        },
        inputPrice: {
            width: 95,
            '& > div > input': {
                textAlign: 'right',
            },
        },
        container: {
            // The alignments below carry meaning — the tick centred, the numbers
            // right — and they stay. The sizes and paddings did not: they were
            // written before the theme said what a table cell is, and put 18-,
            // 15- and 13-pixel type into a dialog whose contract says 12.5.
            '& > table': {
                marginBottom: 20,
            },

            /* table head */
            '& thead > tr': {
                height: 'auto',
            },
            '& th:first-child, & td:first-child': {
                textAlign: 'center',
            },
            '& th:nth-child(2), & td:nth-child(2)': {
                textAlign: 'center',
            },
            '& th:nth-child(1n+4), & td:nth-child(n+4)': {
                textAlign: 'right',
            },

            /* table body */
            '& tbody tr td': {
                verticalAlign: 'top',
            },
        },
    });

interface ConvertReceiptCustomerSupplierOwnProps {
    converterSupplier: ConverterCustomerSupplier;
    convertCustomer: ConverterCustomer;
    converterCustomerIndex: number;
    converterSupplierIndex: number;
    /** The customer this block hangs under is locked — see ConvertReceiptLocked. */
    locked?: boolean;
    formikProps: FormikProps<ConverterJob>;
    showPrice: boolean;
    showTax: boolean;
    showDiscount: boolean;
    showDeliveryCosts: boolean;
}

interface ConvertReceiptCustomerSupplierReduxProps extends WithStyles<typeof styles> {
    currency: string;
    locale: Locale;
    user: User;
}

class ConvertReceiptCustomerSupplierBase extends FormComponent<
    ConverterJob,
    ConvertReceiptCustomerSupplierOwnProps & ConvertReceiptCustomerSupplierReduxProps
> {
    private fieldNamePrefix?: string;

    private renderArticleName = (item: ConverterPositionItem) => {
        const url = item.product && ROUTES.products.product.detail.replace(':id', item.product.id.toString());

        return (
            <ProductDetails
                product={item}
                url={hasRole([UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales], this.props.user) ? url : undefined}
            />
        );
    };

    private renderPositionTotal = (item: ConverterPositionItem) => {
        if (!item.isSelected) {
            return '-';
        }

        const positionTotal = FinancialCalculations.getNetTotal(item);
        return <FormattedNumber value={positionTotal} minimumFractionDigits={2} maximumFractionDigits={2} />;
    };

    private handleClick = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    };

    private isLocked = (): boolean => !!this.props.locked || !!this.props.converterSupplier.isLocked;

    private getRows = () => {
        const { showPrice, showTax, showDiscount } = this.props;
        return this.props.converterSupplier.items.map((item, index) => {
            const prefix = this.fieldNamePrefix + `.items.${index}`;
            return {
                id: item.id,
                pos: index + 1,
                quantity: (
                    <Field name={`${prefix}.quantity`}>
                        {({ field, form }: FieldProps<ConverterCustomer[]>) => (
                            <PoolTextField
                                id="quantity"
                                fieldType="dense"
                                className={this.props.classes.input}
                                // The unit beside the figure, as in the position
                                // tables: as a label above it, it pushed the field
                                // below the price, tax and discount of its row.
                                endAdornment={item.orderUnit}
                                {...field}
                                error={this.hasError(form, field.name)}
                                helperText={this.getError(form, field.name)}
                                disabled={!item.isSelected}
                                onClick={this.handleClick}
                                isNumeric={true}
                            />
                        )}
                    </Field>
                ),
                price: showPrice && (
                    <Field name={`${prefix}.price`}>
                        {({ field, form }: FieldProps<ConverterCustomer[]>) => (
                            <PoolTextField
                                fieldType="dense"
                                className={this.props.classes.inputPrice}
                                error={this.hasError(form, field.name)}
                                helperText={this.getError(form, field.name)}
                                disabled={!item.isSelected}
                                onClick={this.handleClick}
                                isNumeric={true}
                                {...field}
                            />
                        )}
                    </Field>
                ),
                tax: showTax && (
                    <Field name={`${prefix}.tax`}>
                        {({ field, form }: FieldProps<ConverterCustomer[]>) => (
                            <PoolTextField
                                fieldType="dense"
                                className={this.props.classes.inputPrice}
                                error={this.hasError(form, field.name)}
                                helperText={this.getError(form, field.name)}
                                disabled={!item.isSelected}
                                onClick={this.handleClick}
                                isNumeric={true}
                                {...field}
                            />
                        )}
                    </Field>
                ),
                discount: showDiscount && (
                    <Field name={`${prefix}.discount`}>
                        {({ field, form }: FieldProps<ConverterCustomer[]>) => (
                            <PoolTextField
                                fieldType="dense"
                                className={this.props.classes.inputPrice}
                                error={this.hasError(form, field.name)}
                                helperText={this.getError(form, field.name)}
                                disabled={!item.isSelected}
                                isNumeric={true}
                                {...field}
                                onClick={this.handleClick}
                            />
                        )}
                    </Field>
                ),
                total: showPrice && this.renderPositionTotal(item),
                article: this.renderArticleName(item),
            };
        });
    };

    private handleSelected = (selectedRows: number[]) => {
        // A locked partner's positions stay unpicked: this document is not
        // carried on under a partner that may no longer be ordered from.
        if (this.isLocked()) {
            return;
        }

        this.props.converterSupplier.items.forEach((item, index) => {
            const path = this.fieldNamePrefix + `.items.${index}.isSelected`;
            this.props.formikProps.setFieldValue(path, selectedRows.includes(item.id));
        });
    };

    public render() {
        const {
            converterSupplier,
            convertCustomer,
            converterSupplierIndex,
            converterCustomerIndex,
            currency,
            showPrice,
            showTax,
            showDiscount,
            showDeliveryCosts,
        } = this.props;

        const columns: PoolTableColumn[] = [
            { id: 'pos', label: <FormattedMessage id={'app.table.column.positionNumber'} />, sortable: false },
            {
                id: 'article',
                label: <FormattedMessage id={'app.table.column.article'} />,
                sortable: false,
                width: '100%',
            },
            { id: 'quantity', label: <FormattedMessage id={'app.table.column.quantity'} />, sortable: false },
        ];

        if (showPrice) {
            columns.push({
                id: 'price',
                label: (
                    <FormattedMessage
                        id={'app.table.column.priceWithValue'}
                        values={{ value: getCurrencySymbol(this.props.currency, this.props.locale) }}
                    />
                ),
                sortable: false,
            });
        }
        if (showTax) {
            columns.push({
                id: 'tax',
                label: <FormattedMessage id={'app.table.column.taxWithPercentage'} />,
                sortable: false,
            });
        }
        if (showDiscount) {
            columns.push({
                id: 'discount',
                label: <FormattedMessage id={'app.table.column.discountWithPercentage'} />,
                sortable: false,
            });
        }
        if (showPrice) {
            columns.push({
                id: 'total',
                label: (
                    <FormattedMessage
                        id={'app.table.column.positionValue'}
                        values={{ value: getCurrencySymbol(this.props.currency, this.props.locale) }}
                    />
                ),
                sortable: false,
                highlighted: true,
            });
        } else {
            columns.push({ id: 'total', label: '', sortable: false });
        }

        this.fieldNamePrefix = `customers.${converterCustomerIndex}.customerSuppliers.${converterSupplierIndex}`;

        const selected = converterSupplier.items.reduce((result: number[], item) => {
            if (item.id && item.isSelected) {
                result.push(item.id);
            }
            return result;
        }, []);

        return (
            <div className={this.props.classes.container}>
                {converterSupplier.name && (
                    <PoolTypography styleType="sectionTitle" color="inherit">
                        <Box>
                            {dayjs.isDayjs(converterSupplier.name) ? (
                                <FormattedMessage
                                    id="app.convertReceipt.desiredDeliveryDate"
                                    values={{ deliveryDate: converterSupplier.name.format('LL') }}
                                />
                            ) : (
                                converterSupplier.name
                            )}
                            {converterSupplier.isBestOffer && (
                                <Box component="span" sx={{ ml: '8px' }}>
                                    <PaloBestOfferBadge />
                                </Box>
                            )}
                            {converterSupplier.address && (
                                <>
                                    <span>{' \u2022 '}</span>
                                    {converterSupplier.address?.street} {converterSupplier.address?.number}
                                    <span>{' \u2022 '}</span> {converterSupplier.address?.city}
                                </>
                            )}
                        </Box>
                        <PoolTypography typography="label" color="grey.800">
                            {converterSupplier.deliveryDate && (
                                <FormattedMessage
                                    id="app.convertReceipt.desiredDeliveryDate"
                                    values={{ deliveryDate: dayjs(converterSupplier.deliveryDate).format('LL') || '' }}
                                />
                            )}
                            {convertCustomer.customerContact && (
                                <>
                                    <Bullet />
                                    <FormattedMessage
                                        id="app.convertReceipt.orderer"
                                        values={{
                                            orderer: convertCustomer.customerContact.fullName,
                                        }}
                                    />
                                </>
                            )}
                        </PoolTypography>
                    </PoolTypography>
                )}
                {converterSupplier.isLocked && typeof converterSupplier.name === 'string' && (
                    <ConvertReceiptLocked name={converterSupplier.name} role="supplier" />
                )}
                <PoolTable
                    styleType="selectable"
                    columns={columns}
                    rows={this.getRows()}
                    onSelected={this.handleSelected}
                    selected={selected}
                />
                {showPrice && (
                    <ConvertReceiptCustomerSupplierFooter
                        converterSupplier={converterSupplier}
                        converterCustomerIndex={converterCustomerIndex}
                        converterSupplierIndex={converterSupplierIndex}
                        currency={currency}
                        disabled={selected.length === 0}
                        showDeliveryCosts={showDeliveryCosts}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    currency: state.settings.currency,
    locale: bestLocaleSelector(state),
    user: state.user,
});

const ConnectedConvertReceiptCustomerSupplier = connect(mapStateToProps, {})(ConvertReceiptCustomerSupplierBase);

export const ConvertReceiptCustomerSupplier = withStyles(styles)(ConnectedConvertReceiptCustomerSupplier);
